import { cn } from "@tellescope/ui";
import { Check } from "lucide-react";
import { Integration } from "../../types";

export interface IntegrationOrbitItemProps {
  integration: Integration;
  index: number;
  angleDegrees: number;
  isAnimated: boolean;
  isHighlighted: boolean;
  isCompleted: boolean;
  isDisabled: boolean;
}

const getSpokeDashGradient = (color: string) =>
  `repeating-linear-gradient(to top, ${color} 0px, ${color} 4px, transparent 4px, transparent 8px)`;

export const IntegrationOrbitItem = ({
  integration,
  index,
  angleDegrees,
  isAnimated,
  isHighlighted,
  isCompleted,
  isDisabled,
}: IntegrationOrbitItemProps) => {
  const animationDelay = `${index * 20}ms`;

  return (
    <>
      <div
        className="absolute bottom-1/2 left-1/2 z-0 -ml-[2.5px] w-[5px] origin-bottom transition-transform duration-500 ease-in-out"
        style={{ height: "var(--orbit-radius)", transform: `rotate(${angleDegrees}deg)` }}
      >
        <div
          className={cn(
            "relative h-full w-full origin-bottom",
            isAnimated ? "animate-orbit-spoke-in" : "scale-y-0 transition-transform duration-200 ease-out"
          )}
          style={{ animationDelay }}
        >
          {isHighlighted ? (
            <>
              <div
                className="animate-flow-up absolute inset-y-0 left-0 w-0.5"
                style={{ "--color": integration.color, backgroundImage: getSpokeDashGradient("var(--color)") }}
              />
              <div
                className="animate-flow-down absolute inset-y-0 right-0 w-0.5 opacity-50"
                style={{ "--color": integration.color, backgroundImage: getSpokeDashGradient("var(--color)") }}
              />
            </>
          ) : (
            <div className="absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 bg-[#D1D5DB]" />
          )}
        </div>
      </div>
      <div
        className={cn(
          "absolute bottom-1/2 left-1/2 z-10 w-0 origin-bottom transition-transform duration-500 ease-in-out",
          isDisabled && "grayscale"
        )}
        style={{ height: "var(--orbit-radius)", transform: `rotate(${angleDegrees}deg)` }}
      >
        <div
          className="absolute left-0 top-0 transition-transform duration-500 ease-in-out"
          style={{ transform: `translate(-50%, -50%) rotate(${-angleDegrees}deg)` }}
        >
          <div
            className={cn(
              isAnimated ? "animate-orbit-icon-in" : "scale-50 opacity-0 transition-all duration-200 ease-out"
            )}
            style={{ animationDelay }}
          >
            <div
              className={cn(
                "relative flex h-10 w-10 items-center justify-center bg-white rounded-md shadow-md border",
                isHighlighted && "border-2 border-[var(--border-color)]"
              )}
              style={{ "--border-color": integration.color }}
            >
              {integration.Icon ? (
                <integration.Icon className={cn("h-6 w-6 text-gray-700", isDisabled && "opacity-50")} />
              ) : (
                <img
                  src={integration.logoSrc}
                  className={cn("h-6 w-6 max-w-none object-contain", isDisabled && "opacity-50")}
                />
              )}
              {isCompleted && (
                <div className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-green-500 text-white shadow">
                  <Check className="h-2.5 w-2.5" strokeWidth={4} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
